import React, { useState } from 'react';
import { useAuth } from '../../../shared/contexts/AuthContext';
import AnimatedChevron from '../../common/AnimatedChevron';
import type { SidebarMenuItem } from '../../../types/dashboard';
import type { DashboardView } from './Dashboard';
import type { PortalTheme } from '../../../shared/types/dashboard';
import pcLogo from '../../../assets/Images/pc-logo.png';

// Import icons
import HomeIcon from './icons/HomeIcon';
import TransactionsIcon from './icons/TransactionsIcon';
import UserIcon from './icons/UserIcon';
import PacificCrossIcon from './icons/PacificCrossIcon';
import MaintenanceIcon from './icons/MaintenanceIcon';
import AccountIcon from './icons/AccountIcon';
import PasswordIcon from './icons/PasswordIcon';
import AuditIcon from './icons/AuditIcon';
import GuideIcon from './icons/GuideIcon';

interface DashboardSidebarProps {
  currentView?: DashboardView;
  onViewChange?: (view: DashboardView) => void;
  className?: string;
  theme?: PortalTheme;
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  currentView = 'dashboard',
  onViewChange,
  className = '',
  theme = 'admin'
}) => {
  const { user } = useAuth();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [activeItem, setActiveItem] = useState<string>('home');
  const [activeSubItem, setActiveSubItem] = useState<string>('');

  // Get navigation items based on portal theme
  const getNavigationItems = (): SidebarMenuItem[] => {
    if (theme === 'travel-payment') {
      return [
        {
          id: 'home',
          label: 'Home',
          icon: HomeIcon,
          hasDropdown: false
        },
        {
          id: 'transactions',
          label: 'Transactions',
          icon: TransactionsIcon,
          hasDropdown: true,
          subItems: [
            { id: 'new-transaction', label: 'New Transaction' },
            { id: 'transaction-history', label: 'Transaction History' },
            { id: 'payment-status', label: 'Payment Status' }
          ]
        },
        {
          id: 'user',
          label: 'User',
          icon: UserIcon,
          hasDropdown: false
        },
        {
          id: 'account',
          label: 'Account',
          icon: AccountIcon,
          hasDropdown: false
        },
        {
          id: 'change-password',
          label: 'Change Password',
          icon: PasswordIcon,
          hasDropdown: false
        },
        {
          id: 'audit-logs',
          label: 'Audit Logs',
          icon: AuditIcon,
          hasDropdown: false
        },
        {
          id: 'user-guide',
          label: 'User Guide',
          icon: GuideIcon,
          hasDropdown: false
        }
      ];
    }

    // Default admin navigation items
    return [
    {
      id: 'home',
      label: 'Home',
      icon: HomeIcon,
      hasDropdown: false
    },
    {
      id: 'transactions',
      label: 'Transactions',
      icon: TransactionsIcon,
      hasDropdown: true,
      subItems: [
        { id: 'issue-occ', label: 'Issue OCC' },
        { id: 'occ-retrieval', label: 'OCC Retrieval' },
        { id: 'cancelation', label: 'Cancelation' },
        { id: 'cancelled-occ', label: 'Cancelled OCC' },
        { id: 'payment', label: 'Payment' }
      ]
    },
    {
      id: 'user',
      label: 'User',
      icon: UserIcon,
      hasDropdown: true,
      subItems: [
        { id: 'activate-frontliner', label: 'Activate Frontliner' },
        { id: 'deactivate-frontliner', label: 'Deactivate Frontliner' }
      ]
    },
    {
      id: 'pacific-cross',
      label: 'Pacific Cross',
      icon: PacificCrossIcon,
      hasDropdown: true,
      subItems: [
        { id: 'forex', label: 'Forex' },
        { id: 'hbs-upload', label: 'HBS Upload' },
        { id: 'plan-download', label: 'Plan Download' },
        { id: 'plan-upload', label: 'Plan Upload' }
      ]
    },
    {
      id: 'maintenance',
      label: 'Maintenance',
      icon: MaintenanceIcon,
      hasDropdown: true,
      subItems: [
        { id: 'account-executive', label: 'Account Executive' },
        { id: 'travel-agency', label: 'Travel Agency' },
        { id: 'user-maintenance', label: 'User Maintenance' },
        { id: 'group-maintenance', label: 'Group Maintenance' },
        { id: 'itinerary', label: 'Itinerary' },
        { id: 'tax-rate', label: 'Tax Rate' }
      ]
    },
    {
      id: 'account',
      label: 'Account',
      icon: AccountIcon,
      hasDropdown: false
    },
    {
      id: 'change-password',
      label: 'Change Password',
      icon: PasswordIcon,
      hasDropdown: false
    },
    {
      id: 'audit-logs',
      label: 'Audit Logs',
      icon: AuditIcon,
      hasDropdown: false
    },
    {
      id: 'user-guide',
      label: 'User Guide',
      icon: GuideIcon,
      hasDropdown: false
    }
    ];
  };

  const navigationItems = getNavigationItems();

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleMenuItemClick = (itemId: string) => {
    setActiveItem(itemId);
    setActiveSubItem('');
    
    // Handle view changes
    if (itemId === 'account' && onViewChange) {
      onViewChange('account');
    } else if (itemId === 'change-password' && onViewChange) {
      onViewChange('change-password');
    } else if (itemId === 'audit-logs' && onViewChange) {
      onViewChange('audit-logs');
    } else if (itemId === 'home' && onViewChange) {
      onViewChange('dashboard');
    }
    
    const item = navigationItems.find(nav => nav.id === itemId);
    if (item?.hasDropdown) {
      toggleExpanded(itemId);
    }
  };

  const handleSubItemClick = (parentId: string, subItemId: string) => {
    setActiveItem(parentId);
    setActiveSubItem(subItemId);
    
    // Handle specific navigation for Group Maintenance
    if (parentId === 'maintenance' && subItemId === 'group-maintenance' && onViewChange) {
      onViewChange('group-maintenance');
    }
    
    // Handle specific navigation for User Maintenance
    if (parentId === 'maintenance' && subItemId === 'user-maintenance' && onViewChange) {
      onViewChange('user-maintenance');
    }
    
    // Handle specific navigation for Account Executive Maintenance
    if (parentId === 'maintenance' && subItemId === 'account-executive' && onViewChange) {
      onViewChange('account-executive-maintenance');
    }
    
    // Handle specific navigation for Travel Agency Maintenance
    if (parentId === 'maintenance' && subItemId === 'travel-agency' && onViewChange) {
      onViewChange('travel-agency-maintenance');
    }
    
    // Handle specific navigation for Tax Rate Maintenance
    if (parentId === 'maintenance' && subItemId === 'tax-rate' && onViewChange) {
      onViewChange('tax-rate-maintenance');
    }
    
    // Handle specific navigation for Itinerary Maintenance
    if (parentId === 'maintenance' && subItemId === 'itinerary' && onViewChange) {
      onViewChange('itinerary-maintenance');
    }
    
    // Handle specific navigation for Plan Download
    if (parentId === 'pacific-cross' && subItemId === 'plan-download' && onViewChange) {
      onViewChange('plan-download');
    }
    
    // Handle specific navigation for Plan Upload
    if (parentId === 'pacific-cross' && subItemId === 'plan-upload' && onViewChange) {
      onViewChange('plan-upload');
    }
  };

  return (
    <div className={`dashboard-sidebar ${theme === 'travel-payment' ? 'travel-payment-theme' : ''} ${className}`}>
      {/* Brand Section */}
      <div className="sidebar-brand">
        <div className="sidebar-brand-icon">
          <img src={pcLogo} alt="Pacific Cross Logo" className="sidebar-brand-logo" />
        </div>
      </div>

      {/* User Info Section */}
      <div className="sidebar-user-info">
        <div className="sidebar-welcome">Welcome, {user?.name}!</div>
        <div className="sidebar-last-login">Last login: {user?.lastLogin}</div>
      </div>

      {/* Navigation Menu */}
      <nav className="sidebar-nav">
        <ul>
          {navigationItems.map((item) => (
            <li key={item.id} className="sidebar-menu-item">
              <div
                className={`sidebar-menu-link sidebar-menu-link-enhanced ${
                  (item.id === 'account' && currentView === 'account') ||
                  (item.id === 'change-password' && currentView === 'change-password') ||
                  (item.id === 'audit-logs' && currentView === 'audit-logs') ||
                  (item.id === 'home' && currentView === 'dashboard') ||
                  (activeItem === item.id && currentView === 'dashboard') ? 'active' : ''
                }`}
                onClick={() => handleMenuItemClick(item.id)}
              >
                <item.icon className="sidebar-menu-icon sidebar-menu-icon-enhanced" />
                <span className="sidebar-menu-text">{item.label}</span>
                {item.hasDropdown && (
                  <AnimatedChevron
                    isExpanded={expandedItems.includes(item.id)}
                    className="sidebar-chevron"
                  />
                )}
              </div>

              {/* Dropdown Submenu */}
              {item.hasDropdown && item.subItems && (
                <div className={`sidebar-dropdown sidebar-dropdown-enhanced ${expandedItems.includes(item.id) ? 'expanded' : 'collapsed'}`}>
                  <ul className="sidebar-submenu">
                    {item.subItems.map((subItem) => (
                      <li key={subItem.id} className="sidebar-submenu-item sidebar-submenu-item-enhanced">
                        <div
                          className={`sidebar-submenu-link ${activeSubItem === subItem.id ? 'active' : ''}`}
                          onClick={() => handleSubItemClick(item.id, subItem.id)}
                        >
                          {subItem.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

export default DashboardSidebar;